// hooks/useAsthmaStorage.ts
import { useEffect, useRef, useState } from 'react';
import { ActiveView, MedicationLogEntry, AssessmentData, MildSectionKey, SeverityLevel } from '../utils/asthmaTypes';

interface StoredState {
  age: string;
  respiratoryRate: string;
  oxygenSaturation: string;
  oxygenSupport: boolean;
  auscultation: string;
  retractions: string;
  dyspnea: string;
  initialAssessmentTime: string | null;

  // === 1. Added field to Interface ===
  initialData: AssessmentData | null;

  score: number;
  activeSeverity: SeverityLevel | null;
  severityLabel: string;
  treatmentRecommendation: string;

  isAssessmentLocked: boolean;

  medicationLog: MedicationLogEntry[];
  currentMedications: string[];
  secondRoundStarted: boolean;
  thirdRoundStarted: boolean;
  recheckDone: boolean;
  dosesAtStartOfRound2: number;
  dosesAtStartOfRound3: number;
  openSection: MildSectionKey;
  recheckResults: Record<number, AssessmentData>;

  activeView: ActiveView;
  improvedFromSevere: boolean;
  isEmailSent: boolean;
  savedAt?: number;
}

const STORAGE_KEY = 'ASTHMA_APP_DATA_V8';

export const useAsthmaStorage = (setters: any, values: any) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isFirstRender = useRef(true);
  const isJustLoaded = useRef(false);

  // 1. Load
  useEffect(() => {
    try {
      const storedJson = localStorage.getItem(STORAGE_KEY);
      if (storedJson) {
        const parsed: StoredState = JSON.parse(storedJson);

        // Check expiration time (2 hours = 7200000 ms)
        const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
        const now = Date.now();
        const savedTime = parsed.savedAt || 0;

        if (now - savedTime > TWO_HOURS_MS) {
          console.log('Stored data expired (older than 2 hours). Clearing storage.');
          localStorage.removeItem(STORAGE_KEY);
          // Data will not be loaded, function will continue to finally and state will remain empty
        } else {
          const shouldBeLocked = parsed.isAssessmentLocked || parsed.activeView === 'FLOW';
          setters.setIsAssessmentLocked(shouldBeLocked);

          if (parsed.activeSeverity !== undefined) setters.setActiveSeverity(parsed.activeSeverity);
          if (parsed.score !== undefined) setters.setScore(parsed.score);
          if (parsed.severityLabel !== undefined) setters.setSeverity(parsed.severityLabel);
          if (parsed.treatmentRecommendation !== undefined) setters.setTreatmentRecommendation(parsed.treatmentRecommendation);
          setters.setInitialAssessmentTime(parsed.initialAssessmentTime || null);

          // === 2. Loading initial data ===
          if (parsed.initialData !== undefined) {
            setters.setInitialData(parsed.initialData);
          }

          setters.setAge(parsed.age || '');
          setters.setRespiratoryRate(parsed.respiratoryRate || '');
          setters.setOxygenSaturation(parsed.oxygenSaturation || '');
          setters.setOxygenSupport(parsed.oxygenSupport || false);
          setters.setAuscultation(parsed.auscultation || '');
          setters.setRetractions(parsed.retractions || '');
          setters.setDyspnea(parsed.dyspnea || '');

          if (parsed.medicationLog) {
            const restoredLog = parsed.medicationLog.map((entry: any) => ({
              ...entry,
              time: new Date(entry.time)
            }));
            setters.setMedicationLog(restoredLog);
          }

          setters.setCurrentMedications(parsed.currentMedications || []);
          setters.setSecondRoundStarted(parsed.secondRoundStarted || false);
          setters.setThirdRoundStarted(parsed.thirdRoundStarted || false);
          setters.setRecheckDone(parsed.recheckDone || false);
          setters.setDosesAtStartOfRound2(parsed.dosesAtStartOfRound2 || 0);
          setters.setDosesAtStartOfRound3(parsed.dosesAtStartOfRound3 || 0);
          setters.setOpenSection(parsed.openSection || 'INITIAL');
          setters.setRecheckResults(parsed.recheckResults || {});
          setters.setActiveView(parsed.activeView || 'CALCULATOR');
          setters.setImprovedFromSevere(parsed.improvedFromSevere || false);
          setters.setIsEmailSent(parsed.isEmailSent || false);

          // Mark as just loaded to prevent immediate re-save
          isJustLoaded.current = true;
        }
      }
    } catch (error) {
      console.error("Storage Load Error:", error);
    } finally {
      setIsLoaded(true);
      isFirstRender.current = false;
    }
  }, []);

  // 2. Save
  useEffect(() => {
    if (isFirstRender.current || !isLoaded) return;

    // Prevent saving immediately after loading to preserve the original savedAt timestamp
    if (isJustLoaded.current) {
      isJustLoaded.current = false;
      return;
    }

    // Condition for saving
    if (values.age || (values.medicationLog && values.medicationLog.length > 0)) {
      const stateToSave: StoredState = {
        age: values.age,
        respiratoryRate: values.respiratoryRate,
        oxygenSaturation: values.oxygenSaturation,
        oxygenSupport: values.oxygenSupport,
        auscultation: values.auscultation,
        retractions: values.retractions,
        dyspnea: values.dyspnea,
        initialAssessmentTime: values.initialAssessmentTime,

        // === 3. Saving initial data ===
        initialData: values.initialData,

        score: values.score,
        activeSeverity: values.activeSeverity,
        severityLabel: values.severity,
        treatmentRecommendation: values.treatmentRecommendation,
        isAssessmentLocked: values.isAssessmentLocked,

        medicationLog: values.medicationLog,
        currentMedications: values.currentMedications,
        secondRoundStarted: values.secondRoundStarted,
        thirdRoundStarted: values.thirdRoundStarted,
        recheckDone: values.recheckDone,
        dosesAtStartOfRound2: values.dosesAtStartOfRound2,
        dosesAtStartOfRound3: values.dosesAtStartOfRound3,
        openSection: values.openSection,
        recheckResults: values.recheckResults,

        activeView: values.activeView,
        improvedFromSevere: values.improvedFromSevere,
        isEmailSent: values.isEmailSent,
        savedAt: Date.now()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }
  }, [
    values.age, values.respiratoryRate, values.oxygenSaturation, values.oxygenSupport,
    values.auscultation, values.retractions, values.dyspnea, values.initialAssessmentTime,
    values.score, values.activeSeverity, values.severity, values.treatmentRecommendation,
    values.isAssessmentLocked,
    values.medicationLog, values.currentMedications, values.openSection,
    values.secondRoundStarted, values.thirdRoundStarted, values.activeView,
    values.improvedFromSevere, values.isEmailSent, values.recheckDone,
    values.dosesAtStartOfRound2, values.dosesAtStartOfRound3, values.recheckResults,

    // === 4. Adding to dependency list ===
    values.initialData
  ]);

  const clearStorage = () => localStorage.removeItem(STORAGE_KEY);

  const checkExpiration = (): boolean => {
    try {
      const storedJson = localStorage.getItem(STORAGE_KEY);
      if (!storedJson) return false;

      const parsed: StoredState = JSON.parse(storedJson);
      const savedTime = parsed.savedAt || 0;
      const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

      if (Date.now() - savedTime > TWO_HOURS_MS) {
        console.log('checkExpiration: Data expired. Clearing.');
        localStorage.removeItem(STORAGE_KEY);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  return { clearStorage, isLoaded, checkExpiration };
};